<template>
  <div class="deposit-modal">
    <!-- SELECTS -->
    <div class="deposit-selects">
      <div class="select-box">
        <label>Currency</label>
        <select disabled>
          <option>{{ currencyConfig.symbol }}</option>
        </select>
      </div>

      <div class="select-box">
        <label>Network</label>
        <select v-model="selectedNetwork" @change="onNetworkChange">
          <option
            v-for="(net, key) in networks"
            :key="key"
            :value="key"
          >
            {{ net.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- BODY -->
    <div class="deposit-body">
      <div class="qr-box">
        <QRCode
          v-if="address"
          :value="address"
          :options="{ width: 160, height: 160 }"
        />
        <div v-else class="qr-loading"></div>
      </div>

      <div class="address-box">
        <p class="label">
          Your {{ currencyConfig.symbol }} deposit address
        </p>

        <div class="address-input">
          <input :value="address" readonly />
          <button @click="copyAddress">📋</button>
        </div>

        <p class="min">
          Min deposit:
          <b>{{ minDeposit }} {{ currencyConfig.symbol }}</b>
        </p>

        <p class="warning">
          Send only {{ currencyConfig.symbol }} on {{ networkName }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from '@/components/QRCode'
import axios from '@/plugins/axios'

export default {
  name: 'CashierCryptoDeposit',
  components: { QRCode },

  props: {
    currency: {
      type: String,
      required: true
    },
    currencyConfig: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      selectedNetwork: '',
      address: '',
      loading: false
    }
  },

  computed: {
    networks() {
      return this.currencyConfig.networks || {}
    },

    networkConfig() {
      return this.networks[this.selectedNetwork] || null
    },

    networkName() {
      return this.networkConfig?.name || ''
    },

    minDeposit() {
      return this.networkConfig?.deposit_min || 0
    }
  },

  watch: {
    currencyConfig: {
      immediate: true,
      handler() {
        const keys = Object.keys(this.networks)
        this.selectedNetwork = keys[0] || ''
        if (this.selectedNetwork) {
          this.generateAddress()
        }
      }
    }
  },

  methods: {
    async generateAddress() {
      try {
        this.loading = true
        this.address = ''

        const res = await axios.post('/crypto/static-address', {
          coinType: this.currencyConfig.symbol,
          network: this.selectedNetwork
        })

        this.address = res.data.address
      } catch (err) {
        this.$store.dispatch('notificationShow', {
          type: 'error',
          message: 'Failed to generate address'
        })
      } finally {
        this.loading = false
      }
    },

    onNetworkChange() {
      this.generateAddress()
    },

    copyAddress() {
      if (!this.address) return
      navigator.clipboard.writeText(this.address)
      this.$store.dispatch('notificationShow', {
        type: 'success',
        message: 'Address copied'
      })
    }
  }
}
</script>


 <style scoped>
.deposit-modal {
  background: #0e1b28;
  border-radius: 14px;
  padding: 20px;
}

/* SELECTS */
.deposit-selects {
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
}

.select-box {
  flex: 1;
}

.select-box label {
  font-size: 12px;
  color: #9bb0c5;
}

.select-box select {
  width: 100%;
  margin-top: 6px;
  padding: 10px 12px;
  background: #091421;
  border-radius: 8px;
  border: none;
  color: #fff;
}

/* BODY */
.deposit-body {
  display: flex;
  gap: 18px;
}

/* QR */
.qr-box {
  width: 180px;
  height: 180px;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-loading {
  width: 140px;
  height: 140px;
  background: #ddd;
  border-radius: 8px;
}

/* ADDRESS */
.address-box {
  flex: 1;
}

.address-box .label {
  font-size: 13px;
  margin-bottom: 8px;
  color: #cfd9e5;
}

.address-input {
  display: flex;
  align-items: center;
  background: #091421;
  border-radius: 8px;
  overflow: hidden;
}

.address-input input {
  flex: 1;
  padding: 12px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 13px;
}

.address-input button {
  padding: 0 14px;
  font-size: 18px;
  background: none;
  border: none;
  color: #9bb0c5;
  cursor: pointer;
}

/* INFO TEXTS */
.min {
  margin-top: 10px;
  font-size: 12px;
  color: #9bb0c5;
}

.warning {
  margin-top: 6px;
  font-size: 12px;
  color: #f0c36d;
}

/* RESPONSIVE */
@media (max-width: 560px) {
  .deposit-body {
    flex-direction: column;
    align-items: center;
  }

  .qr-box {
    width: 160px;
    height: 160px;
  }

  .deposit-selects {
    flex-direction: column;
  }
}
</style>
